import clsx from "clsx";
import { createContext, FC, PropsWithChildren, useContext } from "react";
import { Link } from "react-router-dom";
import { Asset, Msg } from "rujira.js";
import {
  Button,
  TxButton as TxButtonInner,
  TxButtonProps as TxButtonPropsInner,
} from "rujira.ui";
import { useAccounts } from "../../services/accounts";
import { usePendingDepositStore } from "../../services/deposits";
import { QUERY_CLIENT } from "../../services/queryClient";
import { useBalanceAccount, useDeposit } from "./Deposit";

export type TxButtonProps = Omit<
  TxButtonPropsInner,
  "signer" | "accountProvider"
>;

const context = createContext<Msg | null>(null);

/**
 * MsgProvider is a composable interface to provide a Msg value for a TxButton
 * Nested MsgProviders can adjust the msg finally accessed by the TxButton
 */
export const MsgProvider: FC<PropsWithChildren<{ msg: Msg | null }>> = ({
  children,
  msg,
}) => {
  return <context.Provider value={msg}>{children}</context.Provider>;
};

export const useMsg = () => useContext(context);

export const TxButton: FC<Omit<TxButtonProps, "msg">> = ({
  children,
  onSuccess,
  ...props
}) => {
  const msg = useMsg();
  msg?.withQueryClient?.(QUERY_CLIENT);
  const accountProvider = useAccounts();
  const [setDeposit] = usePendingDepositStore();

  return accountProvider.selected ? (
    <TxButtonInner
      msg={msg}
      accountProvider={accountProvider}
      onSuccess={(x) => {
        if (msg?.toDeposit)
          setDeposit({
            hash: x.txHash,
            network: x.network,
            timestamp: new Date(),
            coin: msg.toDeposit(),
          });
        onSuccess?.(x);
      }}
      {...props}>
      {children}
    </TxButtonInner>
  ) : (
    <Link
      className={clsx({
        [props.className || ""]: true,
        "button button--grey disabled": true,
      })}
      to="/connect">
      Connect Wallet
    </Link>
  );
};

type TxBalanceAccountButtonProps = Omit<
  TxButtonProps,
  "signer" | "accountProvider" | "msg"
> & {
  required: {
    asset: Asset;
    amount: bigint;
  };
};

export const TxBalanceAccountButton: FC<
  TxBalanceAccountButtonProps & {
    Component?: FC<Omit<TxButtonProps, "msg">>;
  }
> = ({
  children,
  onSuccess: onSuccess,
  hideSimulation: hideSimulation,
  required,
  Component = TxButton,
  ...props
}) => {
  const balance = useBalanceAccount(required);
  const { accounts } = useAccounts();
  const deposit = useDeposit(
    required.amount,
    required.asset,
    <p className="ml-1 color-grey fs-12">
      Deposit {required.asset.metadata.symbol} in order to {props.label}, and
      use other Rujira products
    </p>
  );

  if (
    !accounts ||
    accounts.length === 0 ||
    accounts?.every((account) => !/^(sthor1|thor1)/.test(account.address || ""))
  ) {
    const { className, ...rest } = props;
    return (
      <Button
        {...rest}
        className={`${className} button--grey`}
        label={`Connect Wallet`}
        disabled={true}
      />
    );
  }

  return balance ? (
    Component({ ...props, onSuccess, hideSimulation, children })
  ) : (
    <Button
      {...props}
      label={`Deposit ${required.asset.metadata.symbol} to ${props.label}`}
      onClick={deposit}
    />
  );
};
